/**
 * 响应式设备检测工具
 * 
 * 功能：
 * 1. 检测移动端 vs 桌面端（平板归类为桌面端）
 * 2. 监听屏幕尺寸变化
 * 3. 提供响应式工具方法
 * 
 * 设备分类：
 * - 手机（< 768px）= 移动端布局
 * - 平板/电脑（≥ 768px）= 桌面端布局
 */

import { ref, computed, readonly, onMounted, onUnmounted } from 'vue'

// 移动端断点（Ant Design Vue 默认断点）
export const MOBILE_BREAKPOINT = 768

export function useResponsive() {
    // 屏幕宽度（初始化为当前窗口宽度，避免首帧布局错误）
    const screenWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0)

    // 核心判断：是否为移动端布局
    const isMobile = computed(() => screenWidth.value < MOBILE_BREAKPOINT)

    // 设备方向检测（手机横竖屏切换时有用）
    const isLandscape = computed(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth > window.innerHeight
        }
        return false
    })

    // 更新屏幕尺寸
    const updateScreenSize = () => {
        if (typeof window !== 'undefined') {
            screenWidth.value = window.innerWidth
        }
    }

    // 响应式工具：根据设备类型返回不同的值
    const getResponsiveValue = <T>(mobileValue: T, desktopValue: T): T => {
        return isMobile.value ? mobileValue : desktopValue
    }

    // 获取设备类型的CSS类名
    const getDeviceClass = (baseClass = '') => {
        const classes = [baseClass].filter(Boolean)
        classes.push(isMobile.value ? 'mobile-device' : 'desktop-device')
        if (isLandscape.value) classes.push('landscape')
        return classes.join(' ')
    }

    // 事件监听器
    let resizeHandler: (() => void) | null = null
    let orientationHandler: (() => void) | null = null

    // 初始化
    onMounted(() => {
        updateScreenSize()

        // 监听窗口大小变化
        resizeHandler = updateScreenSize
        window.addEventListener('resize', resizeHandler)

        // 监听设备方向变化（主要针对移动端）
        orientationHandler = () => {
            // 延迟更新，等待方向改变完成
            setTimeout(updateScreenSize, 100)
        }
        window.addEventListener('orientationchange', orientationHandler)
    })

    // 清理事件监听器
    onUnmounted(() => {
        if (resizeHandler) {
            window.removeEventListener('resize', resizeHandler)
            resizeHandler = null
        }
        if (orientationHandler) {
            window.removeEventListener('orientationchange', orientationHandler)
            orientationHandler = null
        }
    })

    return {
        // 核心状态（只读）
        isMobile: readonly(isMobile),
        screenWidth: readonly(screenWidth),
        isLandscape: readonly(isLandscape),

        // 工具方法
        getResponsiveValue,
        getDeviceClass,

        // 常量
        MOBILE_BREAKPOINT
    }
}
